import { useState } from "react";
import { Accordion, Badge, Col, Form, Row, Table } from "react-bootstrap";
import AllocationBar, {
  ALLOCATION_BAR_COLORS,
} from "../components/AllocationBar/AllocationBar";
import CopyButton from "../components/CopyButton";
import { formatCurrency, formatDecimal, formatPercent } from "../lib/numbers";
import { getRiskAllocation } from "./lib";
import type {
  V2CompletionAccountDetail,
  V2CompletionStrategyStep,
  V2ResidualCalculation,
  V2RiskLevelRow,
} from "./lib/explainV2Types";

interface CompletionStrategyExplainProps {
  data: V2CompletionStrategyStep;
  accountNames?: Record<string, string>;
  modelNames?: Record<string, string>;
}

const TABLE_STYLE = { fontSize: "0.85rem" } as const;

// --- Calculation Sub-Components ---

const RiskLevelProgressionTable = ({ rows }: { rows: V2RiskLevelRow[] }) => {
  const totalIdeal = rows.reduce((s, r) => s + parseFloat(r.ideal), 0);
  const totalConstrained = rows.reduce(
    (s, r) => s + parseFloat(r.constrained),
    0,
  );
  const totalResidual = rows.reduce((s, r) => s + parseFloat(r.residual), 0);

  return (
    <Table size="sm" bordered className="mb-0" style={TABLE_STYLE}>
      <thead className="table-light">
        <tr>
          <th>Risk Level</th>
          <th className="text-end">Ideal</th>
          <th className="text-end">Constrained</th>
          <th className="text-end">Residual</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const residual = parseFloat(row.residual);
          return (
            <tr key={row.risk_level}>
              <td>{row.risk_level}</td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.ideal), 2)}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.constrained), 2)}
              </td>
              <td
                className={`text-end${residual < 0 ? " text-danger fw-bold" : ""}`}
              >
                {formatCurrency(residual, 2)}
              </td>
            </tr>
          );
        })}
        <tr className="fw-bold">
          <td>Total</td>
          <td className="text-end">{formatCurrency(totalIdeal, 2)}</td>
          <td className="text-end">{formatCurrency(totalConstrained, 2)}</td>
          <td className="text-end">{formatCurrency(totalResidual, 2)}</td>
        </tr>
      </tbody>
    </Table>
  );
};

const ACLevelProgressionTable = ({
  rows,
}: {
  rows: V2ResidualCalculation["ac_level_rows"];
}) => {
  const totalWaterfall = rows.reduce(
    (s, r) => s + parseFloat(r.waterfall_allocated),
    0,
  );

  // Track risk level for visual grouping
  let lastRisk = "";

  return (
    <Table size="sm" bordered className="mb-0" style={TABLE_STYLE}>
      <thead className="table-light">
        <tr>
          <th>Risk</th>
          <th>Asset Class</th>
          <th className="text-end">Ideal</th>
          <th className="text-end">Constrained</th>
          <th className="text-end">Waterfall Allocated</th>
          <th className="text-end">Residual</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const showRisk = row.risk_level !== lastRisk;
          lastRisk = row.risk_level;
          const residual = parseFloat(row.residual);
          return (
            <tr
              key={`${row.risk_level}-${row.asset_class}`}
              style={
                showRisk && idx > 0
                  ? { borderTop: "2px solid #dee2e6" }
                  : undefined
              }
            >
              <td className={showRisk ? "fw-bold" : "text-muted"}>
                {showRisk ? row.risk_level : ""}
              </td>
              <td>{row.asset_class}</td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.ideal), 2)}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.constrained), 2)}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.waterfall_allocated), 2)}
              </td>
              <td
                className={`text-end${residual < 0 ? " text-danger fw-bold" : ""}`}
              >
                {formatCurrency(residual, 2)}
              </td>
            </tr>
          );
        })}
        <tr className="fw-bold" style={{ borderTop: "2px solid #dee2e6" }}>
          <td colSpan={4}></td>
          <td className="text-end">{formatCurrency(totalWaterfall, 2)}</td>
          <td></td>
        </tr>
      </tbody>
    </Table>
  );
};

const SecurityMappingTable = ({
  rows,
}: {
  rows: V2ResidualCalculation["security_mapping_rows"];
}) => {
  const totalWaterfall = rows.reduce(
    (s, r) => s + parseFloat(r.waterfall_dollars),
    0,
  );

  // Sort by asset class then by model weight descending
  const sorted = [...rows].sort((a, b) => {
    const acCmp = a.asset_class.localeCompare(b.asset_class);
    if (acCmp !== 0) return acCmp;
    return parseFloat(b.model_weight) - parseFloat(a.model_weight);
  });

  return (
    <Table size="sm" bordered className="mb-0" style={TABLE_STYLE}>
      <thead className="table-light">
        <tr>
          <th>Asset Class</th>
          <th>Symbol</th>
          <th className="text-end">Model Weight</th>
          <th className="text-end">Target $</th>
          <th className="text-end">Constrained $</th>
          <th className="text-end">Waterfall $</th>
          <th className="text-end">Residual $</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => {
          const residual = parseFloat(row.residual);
          return (
            <tr key={row.symbol}>
              <td>{row.asset_class}</td>
              <td>{row.symbol}</td>
              <td className="text-end">
                {formatDecimal(parseFloat(row.model_weight))}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.target_dollars), 2)}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.constrained_dollars), 2)}
              </td>
              <td className="text-end">
                {formatCurrency(parseFloat(row.waterfall_dollars), 2)}
              </td>
              <td
                className={`text-end${residual < 0 ? " text-danger fw-bold" : ""}`}
              >
                {formatCurrency(residual, 2)}
              </td>
            </tr>
          );
        })}
        <tr className="fw-bold" style={{ borderTop: "2px solid #dee2e6" }}>
          <td colSpan={5}></td>
          <td className="text-end">{formatCurrency(totalWaterfall, 2)}</td>
          <td></td>
        </tr>
      </tbody>
    </Table>
  );
};

const CalculationTables = ({
  calculation,
}: {
  calculation: V2ResidualCalculation;
}) => (
  <>
    <h6 className="text-muted mb-2">1. Risk-Level Residual</h6>
    <div className="mb-3">
      <RiskLevelProgressionTable rows={calculation.risk_level_rows} />
    </div>

    <h6 className="text-muted mb-2">2. Asset Class Allocation</h6>
    <div className="mb-3">
      <ACLevelProgressionTable rows={calculation.ac_level_rows} />
    </div>

    <h6 className="text-muted mb-2">3. Security Mapping</h6>
    <div className="mb-3">
      <SecurityMappingTable rows={calculation.security_mapping_rows} />
    </div>
  </>
);

// --- Main Component ---

const CompletionStrategyExplain = ({
  data,
  accountNames,
  modelNames,
}: CompletionStrategyExplainProps) => {
  const householdAum = parseFloat(data.household_aum);
  const deviationThreshold = 0.05; // 5%
  const verificationPassed =
    Math.abs(parseFloat(data.verification.max_deviation)) <= deviationThreshold;

  // Build merged progression rows
  const progressionByAccount = new Map(
    data.constrained_pool_progression.map((s) => [s.after_account_id, s]),
  );
  const initialTotal = Object.values(data.constrained_pool_initial).reduce(
    (sum, v) => sum + parseFloat(v),
    0,
  );
  const sortedAccounts = data.account_classifications.toSorted(
    (a, b) => a.processing_order - b.processing_order,
  );
  const progressionRows = sortedAccounts.map((acct) => {
    const snapshot = progressionByAccount.get(acct.account_id);
    return {
      order: acct.processing_order,
      accountId: acct.account_id,
      group: acct.processing_group,
      hasModel: acct.has_explicit_model,
      modelId: acct.model_id,
      balance: parseFloat(acct.balance),
      cumulativeConstrained: snapshot
        ? parseFloat(snapshot.total_constrained_amount)
        : acct.has_explicit_model
          ? initialTotal
          : null,
      riskLevel: snapshot?.by_risk_level ?? null,
    };
  });

  const getAccountName = (id: number | string) =>
    accountNames?.[String(id)] || `Account ${id}`;

  return (
    <>
      {/* Section 1: Strategy Overview Banner */}
      <Row className="mb-3">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 className="mb-0">Completion Strategy</h3>
          <CopyButton
            data={data}
            title="Copy completion strategy data to clipboard"
            size={16}
          />
        </div>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Table size="sm" bordered className="mb-0" style={{ width: "auto" }}>
            <tbody>
              <tr>
                <td>
                  <strong>Strategy</strong>
                </td>
                <td>{data.strategy}</td>
              </tr>
              <tr>
                <td>
                  <strong>Household AUM</strong>
                </td>
                <td>{formatCurrency(householdAum, 2)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Verification</strong>
                </td>
                <td>
                  <Badge bg={verificationPassed ? "success" : "danger"}>
                    {verificationPassed ? "PASS" : "FAIL"}
                  </Badge>
                  <span
                    className="ms-2 text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    max deviation:{" "}
                    {formatDecimal(parseFloat(data.verification.max_deviation))}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Section 2: Processing Order & Pool Progression (merged) */}
      <Row className="mb-3">
        <Col>
          <h5>Processing Order & Pool Progression</h5>
          <Table
            size="sm"
            striped
            bordered
            className="mb-0"
            style={TABLE_STYLE}
          >
            <thead className="table-light">
              <tr>
                <th>Order</th>
                <th>Account</th>
                <th>Model</th>
                <th className="text-end">Balance</th>
                <th className="text-end">Cumulative Constrained</th>
                <th style={{ minWidth: "200px" }}>Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {progressionRows.map((row) => (
                <tr key={row.accountId}>
                  <td>{row.order}</td>
                  <td>{getAccountName(row.accountId)}</td>
                  <td>
                    {row.hasModel
                      ? modelNames?.[String(row.accountId)] ||
                        `Model ${row.modelId}`
                      : row.group === "no_model_taxable"
                        ? "None (Taxable)"
                        : "None (Tax Exempt)"}
                  </td>
                  <td className="text-end">{formatCurrency(row.balance, 2)}</td>
                  <td className="text-end">
                    {row.cumulativeConstrained != null
                      ? formatCurrency(row.cumulativeConstrained, 2)
                      : "—"}
                  </td>
                  <td>
                    {row.riskLevel ? (
                      <AllocationBar
                        data={toRiskRecord(row.riskLevel)}
                        converter={getRiskAllocation}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Section 3: Calculations (per no-model account) */}
      {Object.keys(data.residual_models).length > 0 && (
        <Row className="mb-3">
          <Col>
            <h5>Calculations</h5>
            <Accordion>
              {Object.entries(data.residual_models).map(
                ([accountId, residualModel]) => (
                  <Accordion.Item key={accountId} eventKey={accountId}>
                    <Accordion.Header>
                      {getAccountName(accountId)}
                      {residualModel.excluded_symbols.length > 0 && (
                        <Badge bg="secondary" className="ms-2">
                          {residualModel.excluded_symbols.length} excluded
                        </Badge>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <CalculationTables
                        calculation={residualModel.residual_calculation}
                      />

                      {residualModel.excluded_symbols.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-muted mb-2">Excluded Symbols</h6>
                          <span style={TABLE_STYLE}>
                            {residualModel.excluded_symbols.join(", ")}
                          </span>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ),
              )}
            </Accordion>
          </Col>
        </Row>
      )}

      {/* Section 4: Verification Results */}
      <Row className="mb-3">
        <Col>
          <h5>
            Verification{" "}
            <Badge bg={verificationPassed ? "success" : "danger"}>
              {verificationPassed ? "PASS" : "FAIL"}
            </Badge>
          </h5>
          <div className="mb-2" style={TABLE_STYLE}>
            Total deviation:{" "}
            {formatDecimal(parseFloat(data.verification.max_deviation))}
          </div>

          {Object.keys(data.verification.deviations_by_asset_class).length >
            0 && (
            <Table
              size="sm"
              striped
              bordered
              className="mb-3"
              style={TABLE_STYLE}
            >
              <thead className="table-light">
                <tr>
                  <th>Asset Class</th>
                  <th className="text-end">Deviation</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(
                  data.verification.deviations_by_asset_class,
                ).map(([ac, deviation]) => {
                  const deviationNum = parseFloat(deviation);
                  return (
                    <tr key={ac}>
                      <td>{ac}</td>
                      <td
                        className={`text-end${
                          Math.abs(deviationNum) > deviationThreshold
                            ? " text-danger fw-bold"
                            : ""
                        }`}
                      >
                        {formatDecimal(deviationNum)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {data.verification.constraint_impacts.length > 0 && (
            <div style={TABLE_STYLE}>
              <strong>Constraint impacts:</strong>{" "}
              {data.verification.constraint_impacts.length}
            </div>
          )}
        </Col>
      </Row>

      {/* Section 5: Summary */}
      {data.summary.length > 0 && (
        <Row className="mb-3">
          <Col>
            <h5>Summary</h5>
            <ul style={TABLE_STYLE}>
              {data.summary.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </Col>
        </Row>
      )}
    </>
  );
};

// --- Per-account components used inside AccountExplainSection ---

const toRiskRecord = (risk: Record<string, string>): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(risk)) {
    result[key] = parseFloat(value);
  }
  return result;
};

const CompositionBar = ({
  householdRisk,
  constrainedRisk,
  residualRisk,
  constrainedPoolPct,
  residualPoolPct,
}: {
  householdRisk: Record<string, number>;
  constrainedRisk: Record<string, number>;
  residualRisk: Record<string, number>;
  constrainedPoolPct: number;
  residualPoolPct: number;
}) => {
  // Use household normalized values as the source of truth,
  // then split each risk level proportionally between constrained and residual.
  // This guarantees the composition segments sum to the household bar.
  const householdNormalized = getRiskAllocation(householdRisk);
  const riskKeys = Object.keys(householdNormalized);

  const segments: {
    key: string;
    label: string;
    width: number;
    color: string;
    group: "constrained" | "residual";
    borderRadius?: string;
  }[] = [];

  let constrainedTotal = 0;
  let residualTotal = 0;

  for (const key of riskKeys) {
    const hhValue = householdNormalized[key];
    const cRaw = (constrainedRisk[key] ?? 0) * constrainedPoolPct;
    const rRaw = (residualRisk[key] ?? 0) * residualPoolPct;
    const rawSum = cRaw + rRaw;

    const cWidth = rawSum > 0 ? hhValue * (cRaw / rawSum) : 0;
    const rWidth = rawSum > 0 ? hhValue * (rRaw / rawSum) : 0;
    constrainedTotal += cWidth;
    residualTotal += rWidth;

    segments.push({
      key: `c-${key}`,
      label: `${formatPercent(cWidth)} ${key}`,
      width: cWidth,
      color:
        ALLOCATION_BAR_COLORS[
          riskKeys.indexOf(key) % ALLOCATION_BAR_COLORS.length
        ],
      group: "constrained",
    });
    segments.push({
      key: `r-${key}`,
      label: `${formatPercent(rWidth)} ${key}`,
      width: rWidth,
      color:
        ALLOCATION_BAR_COLORS[
          riskKeys.indexOf(key) % ALLOCATION_BAR_COLORS.length
        ],
      group: "residual",
    });
  }

  // Sort: constrained segments first, then residual, preserving risk key order
  const constrained = segments.filter((s) => s.group === "constrained");
  const residual = segments.filter((s) => s.group === "residual");
  if (constrained.length > 0) constrained[0].borderRadius = "5px 0 0 5px";
  if (residual.length > 0)
    residual[residual.length - 1].borderRadius = "0 5px 5px 0";
  const ordered = [...constrained, ...residual];

  if (ordered.length === 0) return null;

  return (
    <div className="d-flex align-items-end">
      <div
        style={{
          width: "120px",
          paddingRight: "12px",
          textAlign: "right",
        }}
      >
        <small className="text-muted">Composition</small>
      </div>
      <div style={{ flex: "1" }}>
        {/* Percentage labels */}
        <div style={{ display: "flex", width: "100%" }}>
          {ordered.map((seg) => (
            <div
              key={`lbl-${seg.key}`}
              style={{
                width: formatPercent(seg.width),
                textAlign: "center",
                fontSize: "0.75rem",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {seg.width >= 0.04 ? formatPercent(seg.width) : ""}
            </div>
          ))}
        </div>
        {/* Bar segments */}
        <div style={{ display: "flex", width: "100%" }}>
          {ordered.map((seg, i) => {
            const isGroupBoundary = i > 0 && ordered[i - 1].group !== seg.group;
            return (
              <div
                key={seg.key}
                className="segment"
                style={{
                  width: formatPercent(seg.width),
                  backgroundColor: seg.color,
                  opacity: seg.group === "constrained" ? 1 : 0.6,
                  borderRadius: seg.borderRadius,
                  borderLeft: isGroupBoundary
                    ? "2px solid rgba(255,255,255,0.6)"
                    : undefined,
                }}
                title={seg.label}
              />
            );
          })}
        </div>
        {/* Group labels below */}
        <div style={{ display: "flex", width: "100%" }}>
          <div
            style={{
              width: formatPercent(constrainedTotal),
              textAlign: "center",
              fontSize: "0.75rem",
            }}
          >
            <small className="text-muted">
              Constrained ({formatPercent(constrainedPoolPct)})
            </small>
          </div>
          <div
            style={{
              width: formatPercent(residualTotal),
              textAlign: "center",
              fontSize: "0.75rem",
            }}
          >
            <small className="text-muted">
              Residual ({formatPercent(residualPoolPct)})
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompletionAssetClassTable = ({
  detail,
}: {
  detail: V2CompletionAccountDetail;
}) => {
  const [pctMode, setPctMode] = useState<"household" | "pool">("household");

  const allAssetClasses = new Set([
    ...Object.keys(detail.household_target_by_asset_class),
    ...Object.keys(detail.constrained_by_asset_class),
    ...Object.keys(detail.residual_by_asset_class),
  ]);

  const hhTotal = Object.values(detail.household_target_by_asset_class).reduce(
    (s, v) => s + parseFloat(v),
    0,
  );
  const conTotal = Object.values(detail.constrained_by_asset_class).reduce(
    (s, v) => s + parseFloat(v),
    0,
  );
  const resTotal = Object.values(detail.residual_by_asset_class).reduce(
    (s, v) => s + parseFloat(v),
    0,
  );

  const isHousehold = pctMode === "household";

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-2">
        <Form.Check
          type="switch"
          id={`completion-pct-mode-${detail.account_id}`}
          label={isHousehold ? "Share of Household" : "Pool Weights"}
          checked={isHousehold}
          onChange={() => setPctMode(isHousehold ? "pool" : "household")}
        />
        <small className="text-muted">
          {isHousehold
            ? "Constrained + Residual shares sum to 1.0 of household"
            : "Constrained and Residual weights each sum to 1.0 within their pool"}
        </small>
      </div>
      <Table
        size="sm"
        striped
        bordered
        className="mb-0"
        style={{ fontSize: "0.85rem" }}
      >
        <thead className="table-light">
          <tr>
            <th>Asset Class</th>
            <th className="text-end">HH Target $</th>
            <th className="text-end">HH Target</th>
            <th className="text-end">Constrained $</th>
            <th className="text-end">Constrained</th>
            <th className="text-end">Residual $</th>
            <th className="text-end">Residual</th>
          </tr>
        </thead>
        <tbody>
          {[...allAssetClasses].sort().map((ac) => {
            const hhVal = parseFloat(
              detail.household_target_by_asset_class[ac] ?? "0",
            );
            const conVal = parseFloat(
              detail.constrained_by_asset_class[ac] ?? "0",
            );
            const resVal = parseFloat(
              detail.residual_by_asset_class[ac] ?? "0",
            );

            const conDenom = isHousehold ? hhTotal : conTotal;
            const resDenom = isHousehold ? hhTotal : resTotal;

            return (
              <tr key={ac}>
                <td>{ac}</td>
                <td className="text-end">{formatCurrency(hhVal, 2)}</td>
                <td className="text-end">
                  {hhTotal > 0 ? formatDecimal(hhVal / hhTotal) : "—"}
                </td>
                <td className="text-end">{formatCurrency(conVal, 2)}</td>
                <td className="text-end">
                  {conDenom > 0 ? formatDecimal(conVal / conDenom) : "—"}
                </td>
                <td className="text-end">{formatCurrency(resVal, 2)}</td>
                <td className="text-end">
                  {resDenom > 0 ? formatDecimal(resVal / resDenom) : "—"}
                </td>
              </tr>
            );
          })}
          <tr className="fw-bold">
            <td>Total</td>
            <td className="text-end">{formatCurrency(hhTotal, 2)}</td>
            <td className="text-end">
              {formatDecimal(parseFloat(detail.pct_of_household))}
            </td>
            <td className="text-end">{formatCurrency(conTotal, 2)}</td>
            <td className="text-end">
              {isHousehold
                ? hhTotal > 0
                  ? formatDecimal(conTotal / hhTotal)
                  : "—"
                : formatDecimal(1)}
            </td>
            <td className="text-end">{formatCurrency(resTotal, 2)}</td>
            <td className="text-end">
              {isHousehold
                ? hhTotal > 0
                  ? formatDecimal(resTotal / hhTotal)
                  : "—"
                : formatDecimal(1)}
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  );
};

export const CompletionAccountSection = ({
  detail,
}: {
  detail: V2CompletionAccountDetail;
}) => {
  const pctOfHousehold = parseFloat(detail.pct_of_household);

  // Compute pool fractions from dollar amounts (correct for multi-account households).
  // Constrained values can go negative when constraints bind — clip to zero so they
  // don't deflate the pool total. Household targets are always non-negative.
  const constrainedDollars = Object.values(
    detail.constrained_by_asset_class,
  ).reduce((s, v) => s + Math.max(0, parseFloat(v)), 0);
  const householdDollars = Object.values(
    detail.household_target_by_asset_class,
  ).reduce((s, v) => s + parseFloat(v), 0);
  const constrainedPoolPct =
    householdDollars > 0 ? constrainedDollars / householdDollars : 0;
  const residualPoolPct = 1 - constrainedPoolPct;
  // Residual accounts inherit the household target; do not render target bars for them.
  const isResidual = detail.role === "residual";

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h5 className="mb-0">Completion Strategy</h5>
        <Badge bg={detail.role === "model" ? "primary" : "info"}>
          {detail.role === "model" ? "Model" : "Residual"}
        </Badge>
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
          {formatDecimal(pctOfHousehold)} of household
        </span>
      </div>

      {/* Risk allocation bars */}
      <Row className="mb-3">
        <Col>
          {/* Household target bar — hidden for residual accounts */}
          {!isResidual && (
            <div className="mb-3 d-flex align-items-end">
              <div
                style={{
                  width: "120px",
                  paddingRight: "12px",
                  textAlign: "right",
                }}
              >
                <small className="text-muted">Household</small>
              </div>
              <div style={{ flex: "1" }}>
                <AllocationBar
                  data={toRiskRecord(detail.household_risk)}
                  converter={getRiskAllocation}
                />
              </div>
            </div>
          )}
          <CompositionBar
            householdRisk={toRiskRecord(detail.household_risk)}
            constrainedRisk={toRiskRecord(detail.constrained_risk)}
            residualRisk={toRiskRecord(detail.residual_risk)}
            constrainedPoolPct={constrainedPoolPct}
            residualPoolPct={residualPoolPct}
          />
        </Col>
      </Row>

      {/* Asset class distribution table */}
      <CompletionAssetClassTable detail={detail} />
    </div>
  );
};

export default CompletionStrategyExplain;
